import type { PlaylistTag } from 'types/playlist'
import type { Thunk } from 'types/redux'
import api from '@disco/api/server'
import { PLAYLIST_TAGS_EDIT } from '@disco/constants/ActionTypes'
import { pushUINotification } from '@disco/utils/notifications'
/**
 * Creates a new playlist tag with the specified name.
 * @param name - The name for the new playlist tag
 * @returns Thunk that creates the tag and shows success/error notifications
 */
export function createPlaylistTag(name: string): Thunk {
  return async dispatch => {
    try {
      const updatedTag = await api.createPlaylistTag(name)
      dispatch({
        type: PLAYLIST_TAGS_EDIT,
        id: null,
        tag: updatedTag,
      })
      pushUINotification({
        title: 'New tag added!',
        message: `“${name}”`,
        level: 'success',
      })
      return updatedTag
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Saving failed',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
/**
 * Updates an existing playlist tag with new data.
 * @param tag - The playlist tag to update
 * @returns Thunk that updates the tag and shows success/error notifications
 */
export function editPlaylistTag(tag: PlaylistTag): Thunk {
  return async dispatch => {
    try {
      const updatedTag = await api.editPlaylistTag(tag)
      dispatch({
        type: PLAYLIST_TAGS_EDIT,
        id: tag.id,
        tag: updatedTag,
      })
      pushUINotification({
        title: 'Tag edited!',
        message: `“${tag.name}”`,
        level: 'success',
      })
      return updatedTag
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Saving failed',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
/**
 * Deletes a playlist tag from the system.
 * @param tag - The playlist tag to delete
 * @returns Thunk that deletes the tag and shows success/error notifications
 */
export function deletePlaylistTag(tag: PlaylistTag): Thunk {
  return async dispatch => {
    try {
      await api.deletePlaylistTag(tag.id)
      dispatch({
        type: PLAYLIST_TAGS_EDIT,
        id: tag.id,
        tag: null,
      })
      pushUINotification({
        title: 'Tag removed!',
        message: `“${tag.name}”`,
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: error?.body?.detail || 'Failed to remove',
        level: 'error',
      })
      throw new Error(error)
    }
  }
}
